// Copyright ©️ 2026 eVolve MEP, LLC

import { Checkbox, Flex, Loader, Text } from '@mantine/core';

import { AccordionControlsWithActions } from 'components/Mantine/AccordionControlsWithActions';
import { isNotNil } from 'helpers/isNotNil';
import { VisibilityToggle } from 'modules/Shop/Spooling/Assigning/VisibilityToggle';

type Props = {
  label: string;
  /* The number of spools inside the Accordion.Item this Accordion.Control controls.*/
  itemCount: number | null;
  loading?: boolean;
  visibilityProps: {
    disabled?: boolean;
    visible: boolean;
    toggleAll: (visible: boolean) => void;
  };
  /* if provided, a checkbox controlling selection will be rendered to the left of the Accordion.Control */
  selectionProps?: {
    disabled?: boolean;
    toggleAll: () => void;
    type: 'indeterminate' | 'checked' | 'unchecked';
  };
};

/**
 * Renders an Accordion.Controls component with visibility controls.
 */
export const SpoolAccordionControls = ({
  label,
  itemCount,
  loading = false,
  visibilityProps,
  selectionProps,
}: Props) => (
  <AccordionControlsWithActions
    leftSection={
      isNotNil(selectionProps) ? (
        <Checkbox
          disabled={selectionProps.disabled ?? false}
          onChange={selectionProps.toggleAll}
          onClick={(e) => e.stopPropagation()}
          indeterminate={selectionProps.type === 'indeterminate'}
          checked={selectionProps.type === 'checked'}
        />
      ) : undefined
    }
    rightSection={
      <VisibilityToggle
        visible={visibilityProps.visible}
        disabled={visibilityProps.disabled ?? false}
        onToggle={visibilityProps.toggleAll}
      />
    }
  >
    <Flex gap="xs" align="center">
      <Text pl="xl" fw="bold">
        {label}
      </Text>
      {loading ? (
        <Loader size="xs" />
      ) : (
        isNotNil(itemCount) && (
          <Text c="dimmed" ml="xs">
            ({itemCount.toLocaleString()})
          </Text>
        )
      )}
    </Flex>
  </AccordionControlsWithActions>
);
